import React, { useState } from 'react';
import FileTree from './components/FileTree';

const API_BASE_URL = '/api';

interface FileNode {
  name: string;
  type: 'file' | 'directory';
  path: string;
  size?: number;
  lastModified?: Date;
  children?: FileNode[];
}

function App() {
  const [files, setFiles] = useState<FileNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  console.log('App: current files state:', files);

  const fetchFiles = async () => {
    console.log('App: fetching initial files');
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/list-objects`);
      if (!response.ok) {
        throw new Error('Failed to fetch files');
      }
      const data = await response.json();
      console.log('App: received initial files:', data);
      setFiles(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch files';
      console.error('App: error fetching files:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleFileClick = async (node: FileNode) => {
    console.log('App: handling click for node:', node);
    
    if (node.type === 'directory') {
      try {
        // Ensure the path ends with a slash for directories
        const path = node.path.endsWith('/') ? node.path : node.path + '/';
        console.log('App: fetching directory contents for path:', path);
        
        const response = await fetch(`${API_BASE_URL}/list-objects?prefix=${encodeURIComponent(path)}`);
        if (!response.ok) {
          throw new Error('Failed to fetch directory contents');
        }
        const data = await response.json();
        console.log('App: received directory contents:', data);
        
        // Update the state with the new children
        setFiles(prevFiles => {
          console.log('App: updating files state with new children');
          const updateNode = (nodes: FileNode[]): FileNode[] => {
            return nodes.map(n => {
              if (n.path === node.path) {
                console.log('App: updating node:', n.path);
                const updatedNode: FileNode = {
                  ...n,
                  type: 'directory' as const,
                  children: Array.isArray(data) ? data as FileNode[] : []
                };
                console.log('App: updated node:', updatedNode);
                return updatedNode;
              }
              if (n.children) {
                return { ...n, children: updateNode(n.children) };
              }
              return n;
            });
          };
          const newFiles = updateNode(prevFiles);
          console.log('App: new files state:', newFiles);
          return newFiles;
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch directory contents';
        console.error('App: error fetching directory contents:', errorMessage);
        setError(errorMessage);
      }
    } else {
      // Handle file download
      const downloadUrl = `${API_BASE_URL}/download?key=${encodeURIComponent(node.path)}`;
      console.log('App: opening file download URL:', downloadUrl);
      window.open(downloadUrl, '_blank');
    }
  };

  // Fetch files on component mount
  React.useEffect(() => {
    console.log('App: component mounted, fetching files');
    fetchFiles();
  }, []);

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">S3 File Browser</h1>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-8">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-8">
            <div className="text-gray-600">Loading...</div>
          </div>
        ) : files.length > 0 ? (
          <FileTree files={files} onFileClick={handleFileClick} />
        ) : (
          <div className="text-center py-8">
            <div className="text-gray-600">No files found</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App; 